import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { Footer } from "./components/Footer";
import { Masthead } from "./components/Masthead";
import { ContentProvider, useCatalogue } from "./data/ContentProvider";
import { useLocalizedPath } from "./localization/urls";
import {
  applyDocumentSeo,
  buildStructuredData,
  DEFAULT_SOCIAL_IMAGE,
  localizedCatalogueItems,
  localizedUrl,
  seoLanguage,
  treeSeoImage,
  truncateDescription,
} from "./seo/metadata";
import { applyTheme, initialTheme, type Theme } from "./theme/theme";
import { Home } from "./views/Home";
import { NotFound } from "./views/NotFound";

const Ledger = lazy(() => import("./views/Ledger").then((module) => ({ default: module.Ledger })));
const MapView = lazy(() => import("./views/MapView").then((module) => ({ default: module.MapView })));
const TreeSheet = lazy(() => import("./views/TreeSheet").then((module) => ({ default: module.TreeSheet })));

function LegacyUnconfirmedRedirect() {
  const path = useLocalizedPath();
  return <Navigate replace to={path("/catalog")} />;
}

function HomeRedirect() {
  const path = useLocalizedPath();
  return <Navigate replace to={path("/")} />;
}

function TreeRedirect({ to }: { to: string }) {
  const path = useLocalizedPath();
  return <Navigate replace to={path(to)} />;
}

function DocumentMetadata() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { state, trees, content } = useCatalogue();
  useEffect(() => {
    if (state === "loading" && pathname.startsWith("/tree/")) return;

    const language = seoLanguage(i18n.language);
    const tree = pathname.startsWith("/tree/") ? trees.find((candidate) => `/tree/${candidate.id}` === pathname) : undefined;
    const isTreePath = pathname.startsWith("/tree/");
    const canonicalPath = pathname === "/about" ? "/" : pathname === "/unconfirmed" ? "/catalog" : pathname;
    const kind = tree ? "tree" : canonicalPath === "/" ? "home" : canonicalPath === "/catalog" ? "catalogue" : canonicalPath === "/map" ? "map" : "notFound";
    const treeName = tree ? content(tree.keys.name) : "";
    const village = tree ? content(tree.keys.village) || tree.location.village || "" : "";
    const district = tree ? t(`district.${tree.location.district}`) : "";
    const placeName = village || district;
    const siteTitle = t("site.title");
    const title = tree
      ? `${treeName} — ${siteTitle}`
      : kind === "home"
        ? siteTitle
        : kind === "catalogue"
          ? `${t("catalogue.title")} — ${siteTitle}`
          : kind === "map"
            ? `${t("map.title")} — ${siteTitle}`
            : `${t("notFound.page")} — ${siteTitle}`;
    const description = truncateDescription(
      tree
        ? content(tree.keys.description) || content(tree.keys.tagline) || t("meta.treeDescription", { name: treeName, place: placeName })
        : kind === "catalogue"
          ? t("meta.catalogueDescription", { count: trees.length })
          : kind === "map"
            ? t("meta.mapDescription")
            : t("meta.homeDescription"),
    );
    const image = tree ? treeSeoImage(tree) : { url: DEFAULT_SOCIAL_IMAGE, width: 1200, height: 630, type: "image/png" };
    const url = localizedUrl(canonicalPath, language);

    applyDocumentSeo({
      title,
      description,
      pathname: canonicalPath,
      language,
      image,
      imageAlt: tree ? `${treeName} — ${siteTitle}` : t("meta.imageAlt"),
      type: tree ? "article" : "website",
      noindex: kind === "notFound" || (isTreePath && !tree),
      structuredData: buildStructuredData({
        kind,
        url,
        title,
        description,
        language,
        image,
        catalogueItems: localizedCatalogueItems(trees, (candidate) => content(candidate.keys.name)),
        tree,
        treeName,
        placeName,
        siteDescription: t("meta.homeDescription"),
      }),
    });
  }, [pathname, state, trees, content, t, i18n.language]);
  return null;
}

function Shell() {
  const isMap = useLocation().pathname === "/map";
  const [theme, setTheme] = useState<Theme>(initialTheme);
  useEffect(() => applyTheme(theme, false), [theme]);
  const toggleTheme = useCallback(() => setTheme((current) => {
    const next = current === "dark" ? "light" : "dark";
    applyTheme(next, true);
    return next;
  }), []);
  return (
    <div className={isMap ? "app-shell-map" : undefined}>
      <DocumentMetadata />
      <Masthead theme={theme} onToggleTheme={toggleTheme} />
      <main className={isMap ? "map-page-shell" : "wrap"}>
        <Suspense fallback={<div className="route-loading" aria-busy="true" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/catalog" element={<Ledger />} />
            <Route path="/unconfirmed" element={<LegacyUnconfirmedRedirect />} />
            <Route path="/about" element={<HomeRedirect />} />
            <Route path="/map" element={<MapView />} />
            <Route path="/tree/laneia" element={<TreeRedirect to="/tree/old-oak" />} />
            <Route path="/tree/laneia-centuries-old-oak-start-pin" element={<TreeRedirect to="/tree/old-oak" />} />
            <Route path="/tree/:id" element={<TreeSheet />} />
            <Route path="*" element={<NotFound messageKey="notFound.page" />} />
          </Routes>
        </Suspense>
      </main>
      {!isMap && <Footer />}
    </div>
  );
}

export default function App() {
  return <BrowserRouter><ContentProvider><Shell /></ContentProvider></BrowserRouter>;
}
